<template>
    <div id="invoice" class="mcg">
        <!-- Page 1: cover letter -->
        <div class="mcg-page">
            <McGeoughsLetterhead />
            <div class="mcg-block" style="margin-top:14px;">
                <div v-for="(line, i) in recipient_lines" :key="'r1' + i">{{ line }}</div>
            </div>
            <p class="mcg-gap" contenteditable>{{ invoice_date }}</p>
            <p class="mcg-gap" contenteditable>Dear {{ rep_first_name }}</p>
            <p class="mcg-gap" contenteditable>
                We would like to thank you for entrusting us with the funeral of your late {{ relationship }}
                {{ deceased_name }}. We hope our service more than fulfilled your expectations and that all
                details have been satisfactorily concluded.
            </p>
            <p class="mcg-gap" contenteditable>
                Enclosed you will find copy of the funeral invoice. If payment has already been made, please
                retain this invoice for your records. Our preferred method of payment would be transfer
                IBAN IE19 AIBK 9324 5094 6120 53 BIC AIBKIE2D, please include your name on the payment, to
                ensure the payment in correctly allocated.
            </p>
            <p class="mcg-gap" contenteditable>
                We can also accept payment by Cheque/Bank Draft, Cash, Credit/Debit Card, in person at our
                office or by the telephone 042 9334283
            </p>
            <p class="mcg-gap" contenteditable>
                If you require any further assistance with any aspect of the funeral arrangements or the
                invoice, please do not hesitate to contact us.
            </p>
            <p class="mcg-gap" contenteditable>
                Again, we wish to thank you for selecting us and to assure you always of our best service
                and personal attention
            </p>
            <p class="mcg-gap" style="margin-bottom:60px;">Yours faithfully</p>
            <div class="mcg-signature">
                <div>Oisin and Rosemary Rogers</div>
                <div>McGeoughs Funerals</div>
            </div>
        </div>

        <div class="html2pdf__page-break"></div>

        <!-- Page 2: funeral expenses -->
        <div class="mcg-page">
            <McGeoughsLetterhead />
            <div class="mcg-block" style="margin-top:14px;">
                <div v-for="(line, i) in recipient_lines" :key="'r2' + i">{{ line }}</div>
            </div>
            <p class="mcg-gap" contenteditable>{{ invoice_date }}</p>
            <p class="mcg-gap" contenteditable>
                Funeral expenses of your late {{ relationship }} {{ deceased_name }}
                <br>
                Who died on {{ date_of_death }}<template v-if="place_of_death"> at {{ place_of_death }}</template>.
            </p>
            <p class="mcg-gap mcg-italic" contenteditable v-html="notes_html"></p>

            <div class="mcg-gap">
                <div v-for="(item, index) in funeral_director_payments" :key="'fd' + index" class="mcg-line">
                    <span class="mcg-desc" contenteditable>{{ item.name }}</span>
                    <span class="mcg-amount" contenteditable>{{ formatAmount(item.amount) }}</span>
                </div>
            </div>

            <div class="mcg-gap" v-if="payments_made.length > 0">
                <div class="mcg-underline">Paid on your behalf</div>
                <div class="mcg-gap-sm">
                    <div v-for="(item, index) in payments_made" :key="'pm' + index" class="mcg-line">
                        <span class="mcg-desc" contenteditable>{{ item.name }}</span>
                        <span class="mcg-amount" contenteditable>{{ formatAmount(item.amount) }}</span>
                    </div>
                </div>
            </div>

            <div class="mcg-gap mcg-line">
                <span class="mcg-desc">TOTAL</span>
                <span class="mcg-amount">{{ formatAmount(total) }}</span>
            </div>

            <p class="mcg-gap" style="margin-bottom:0;" contenteditable>
                Payment of outstanding invoice within 30 day would be very much appreciated
                <br>
                IBAN IE19 AIBK 9324 5094 6120 53 BIC AIBKIE2D
            </p>
        </div>
    </div>
</template>

<script>
import McGeoughsLetterhead from './McGeoughsLetterhead.vue'
import { DEFAULT_CHARGES_NOTE, dateParts, splitAddress, parentLabel } from './mcgeoughs'

export default {
    components: { McGeoughsLetterhead },
    props: {
        invoice: { type: Object, required: true },
        total: { type: Number, default: 0 },
    },
    computed: {
        user() {
            return this.invoice.user || {}
        },
        rep() {
            return this.user.rep_details || {}
        },
        deceased_name() {
            return this.user.name
        },
        rep_first_name() {
            return this.rep.first_name || ''
        },
        recipient_lines() {
            const name = [this.rep.first_name, this.rep.last_name].filter(Boolean).join(' ')
            return [name || 'Reps of ' + this.user.name, ...splitAddress(this.rep.address)]
        },
        relationship() {
            return parentLabel(this.user.gender_id)
        },
        invoice_date() {
            return this.longDate(null)
        },
        date_of_death() {
            return this.longDate(this.user.date_of_death)
        },
        place_of_death() {
            return this.user.place_of_death
        },
        funeral_director_payments() {
            return this.invoice.funeral_director_payments || []
        },
        payments_made() {
            return this.invoice.payments_made || []
        },
        notes_html() {
            return this.invoice.notes || DEFAULT_CHARGES_NOTE
        },
    },
    methods: {
        longDate(value) {
            const d = dateParts(value)
            return d ? `${d.day}${d.ordinal} ${d.month} ${d.year}` : ''
        },
        formatAmount(amount) {
            const n = parseFloat(amount)
            return n > 0 ? '€' + n.toFixed(2) : ''
        },
    },
}
</script>

<style scoped lang="scss">
.mcg {
    width: 794px;
    background: white;
    color: black;
    font-family: 'Times New Roman', Times, serif;
    font-size: 15px;
    line-height: 1.25;
}

.mcg-page {
    position: relative;
    box-sizing: border-box;
    width: 794px;
    // Slightly under A4 (1123px at 96dpi) so rounding never spills onto an extra blank PDF page
    height: 1090px;
    padding: 40px 70px;
    overflow: hidden;
}

.mcg-gap {
    margin: 0 0 18px;
}

.mcg-gap-sm {
    margin-top: 10px;
}

.mcg-italic {
    font-style: italic;
}

.mcg-underline {
    text-decoration: underline;
}

.mcg-signature {
    border-top: 2px solid black;
    padding-top: 4px;
    width: 510px;
}

.mcg-line {
    display: flex;
    justify-content: space-between;
}

.mcg-desc {
    flex: 1;
    padding-right: 20px;
}

.mcg-amount {
    min-width: 90px;
    text-align: right;
}
</style>
